import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BdAdministrativeDistrictStore } from '../store/administrative-district.store';
import { BdAdministrativeDistrictCodeCount } from './administrative-district';
import { BdAdministrativeDistrictCache } from './administrative-district.cache';
import { BdAdministrativeDistrictFilter } from './administrative-district.filter';
import {
  BD_ADMINISTRATIVE_DISTRICTS_LAYER_CONFIG,
  BD_ADMINISTRATIVE_DISTRICTS_SOURCE,
  BdAdministrativeDistrict,
  BdAdministrativeDistrictId,
} from '@bindoc-projects/geographic-domain-lib';
import {
  AysCode,
  AysCodeCodingSystem,
  AysCodeResponse,
} from '@bindoc-projects/code-domain-lib';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';

const MAPBOX_TILESET_URL_PREFIX = 'mapbox://';

/** The slice of a Mapbox Tilequery response feature this service reads. */
interface MapboxTilequeryResponse {
  features?: { properties?: { key?: string } }[];
}

@Injectable({
  providedIn: 'root',
})
export class BdAdministrativeDistrictService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _districtCache = inject(BdAdministrativeDistrictCache);
  private readonly _store: BdAdministrativeDistrictStore;
  private readonly _mapboxAccessToken: string;
  private readonly _districtTilesetId =
    BD_ADMINISTRATIVE_DISTRICTS_SOURCE.url?.replace(
      MAPBOX_TILESET_URL_PREFIX,
      '',
    ) ?? '';
  /** The tileset's vector *source-layer* name — distinct from (if, here,
   * spelled the same as) the mapbox-gl *style layer id* used for rendering. */
  private readonly _districtSourceLayer =
    BD_ADMINISTRATIVE_DISTRICTS_LAYER_CONFIG['source-layer'] ?? '';

  constructor() {
    const environmentService = inject(BdAppEnvironmentService);
    this._store = new BdAdministrativeDistrictStore(
      environmentService.load(ApiEndpointKey.BD_DISTRICT_URL),
    );
    this._mapboxAccessToken = environmentService.load(
      'BD_MAP_ACCESS_TOKEN_MAPBOX',
    );
  }

  public find(
    filter: BdAdministrativeDistrictFilter = new BdAdministrativeDistrictFilter(),
  ): Observable<BdAdministrativeDistrict[]> {
    const url = this._store.index();

    return this._httpClient
      .get(url, { params: filter.toHttpParams() })
      .pipe(
        map((res) =>
          BdAdministrativeDistrict.fromObjectArray(res as unknown[]),
        ),
      );
  }

  public findAll(
    administrativeDistrictIds: BdAdministrativeDistrictId[],
  ): Observable<{
    [administrativeDistrictId: string]: BdAdministrativeDistrict;
  }> {
    const url = this._store.index();

    return this._httpClient
      .post<{
        [administrativeDistrictId: string]: {
          id: number;
          name: string;
          otherName: string;
        };
      }>(url, {
        administrativeDistrictIds: administrativeDistrictIds.map((it) =>
          it.getNumericValue(),
        ),
      })
      .pipe(
        map((res) => {
          const result: {
            [administrativeDistrictId: string]: BdAdministrativeDistrict;
          } = {};
          Object.keys(res).forEach(
            (key) =>
              (result[key] = BdAdministrativeDistrict.fromObject(res[key])),
          );
          return result;
        }),
      );
  }

  public byAdministrativeDistrictId(
    administrativeDistrictId: BdAdministrativeDistrictId,
  ): Observable<BdAdministrativeDistrict> {
    const cached = this._districtCache.get(administrativeDistrictId);
    let result;

    if (cached) {
      result = of(cached);
    } else {
      const url = this._store.byAdministrativeDistrictId(
        administrativeDistrictId.value,
      );

      result = this._httpClient.get(url).pipe(
        map((res) => BdAdministrativeDistrict.fromObject(res)),
        map((district) => {
          this._districtCache.set(district);

          return district;
        }),
      );
    }

    return result;
  }

  /**
   * Resolves the Landkreis containing a `[longitude, latitude]` point via a
   * Mapbox Tilequery lookup against the same vector tileset the district map
   * layers render, so it stays in sync with what's shown on screen. Used to
   * auto-fill the virtual-clinic address form's Landkreis field from a
   * geocoded address, analogous to that form's coordinate auto-fill.
   *
   * Resolves to `null` — never errors — when there is no district at that
   * point, the lookup fails, or no Mapbox token is configured, so a failed
   * lookup never wipes a value the user already picked.
   */
  public findByLocation(
    location: [number, number],
  ): Observable<BdAdministrativeDistrict | null> {
    const [lng, lat] = location;
    // The coordinates are a path segment, not a query value — Mapbox's
    // Tilequery router 404s on a percent-encoded comma there, so this must
    // stay a literal `,` (unlike a query-string comma, which either form
    // would parse fine).
    const url =
      `https://api.mapbox.com/v4/${this._districtTilesetId}/tilequery/` +
      `${lng},${lat}.json` +
      `?radius=0&limit=1&geometry=polygon&layers=${this._districtSourceLayer}` +
      `&access_token=${this._mapboxAccessToken}`;

    return this._httpClient.get<MapboxTilequeryResponse>(url).pipe(
      map((response) => response.features?.[0]?.properties?.key),
      map((key) => (key ? BdAdministrativeDistrictId.create(key) : null)),
      switchMap((districtId) =>
        districtId ? this.byAdministrativeDistrictId(districtId) : of(null),
      ),
      catchError((error) => {
        console.warn(
          'BdAdministrativeDistrictService.findByLocation failed',
          error,
        );
        return of(null);
      }),
    );
  }

  public findPostalCodes(
    administrativeDistrictIds: BdAdministrativeDistrictId[],
  ): Observable<string[]> {
    const url = this._store.postalCodes();
    const body = {
      administrativeDistrictIds: administrativeDistrictIds.map((it) =>
        it.getNumericValue(),
      ),
    };

    return this._httpClient.post<string[]>(url, body);
  }

  public findCenterPoint(
    administrativeDistrictIds: BdAdministrativeDistrictId[],
  ): Observable<
    Map<
      BdAdministrativeDistrictId,
      {
        lat: number;
        lon: number;
      }
    >
  > {
    const url = this._store.centerPoints();
    const body: {
      administrativeDistrictIds: number[];
    } = {
      administrativeDistrictIds: administrativeDistrictIds.map((it) =>
        it.getNumericValue(),
      ),
    };
    return this._httpClient
      .post<{
        [administrativeDistrictId: string]: { lat: number; lon: number };
      }>(url, body)
      .pipe(
        map((response) => {
          const result: Map<
            BdAdministrativeDistrictId,
            {
              lat: number;
              lon: number;
            }
          > = new Map<
            BdAdministrativeDistrictId,
            { lat: number; lon: number }
          >();

          Object.entries(response).forEach((it) => {
            result.set(
              BdAdministrativeDistrictId.createFromString(it[0]),
              it[1],
            );
          });

          return result;
        }),
      );
  }

  public cases(
    administrativeDistrictIds: BdAdministrativeDistrictId[],
    codingSystem: AysCodeCodingSystem,
    codes: AysCode[],
    years: number[],
  ): Observable<BdAdministrativeDistrictCodeCount[]> {
    if (!codes.every((it) => it.codingSystem === codingSystem)) {
      console.error(
        `type of some codes did not match ${codingSystem}, returning empty array !`,
      );
      return of([]);
    }

    const url = this._store.casesAll(codingSystem);
    const body = {
      administrativeDistrictIds: administrativeDistrictIds.map((it) =>
        it.getNumericValue(),
      ),
      codes: codes,
      years: years,
    };

    return this._httpClient
      .post<BdAdministrativeDistrictCodeCountResponse[]>(url, body)
      .pipe(
        map((res) =>
          res.map((it) => {
            const item: BdAdministrativeDistrictCodeCount = {
              code: AysCode.fromObject(it.code),
              administrativeDistrictId:
                BdAdministrativeDistrictId.createFromNumber(
                  it.administrativeDistrictId,
                ),
              year: parseInt(it.year, 10),
              count: it.count,
              agmId: it.agmId,
            };
            return item;
          }),
        ),
      );
  }
}

interface BdAdministrativeDistrictCodeCountResponse {
  code: AysCodeResponse;
  count: number;
  administrativeDistrictId: number;
  year: string;
  agmId: number;
}
